import {api} from "@/api/client";
import type {AxiosResponse} from "axios";
import dayjs from "dayjs";


export interface ConversationItem {
    chat_id: number;
    user: {
        id: string;
        name: string;
        avatar_url: string;
        online: boolean;
    };
    last_message: {
        user_id: string;
        text: string;
        timestamp: string;
        type: "media" | "text";
        is_deleted?: boolean;
        deleted_at?: string | null;
        reply_to_message_id?: number | null;
    };
    is_pinned: boolean;
    unread_count: number;
}

export interface MatchItem {
    avatar_url: string;
    id: string;
    name: string;
    is_online?: number | boolean;
}

export interface LastMatch {
    user_id: string;
    is_online: number | boolean;
    name: string;
    user_image: string;
    conversation_id: number | null;
}

export interface Conversation {
    data:{
        conversations: ConversationItem[];
        match?: MatchItem[];
        match_count: number;
    }
}


export interface MessageNewApi {
    id?: number;
    created_at:string;
    sender_id: string;
    message: string | null;
    type: string;
    is_read: boolean;
    gift: string | null;
    contact_type: string | null;
    file_url?: string;
    fid?: string | null;
    file_id?: string | null;
    file_fid?: string | null;
    media_fid?: string | null;
    file_name?: string | null;
    file_mime?: string | null;
    file_size?: number | null;
    file_extension?: string;
    is_image?: boolean;
    is_video?: boolean;
    is_document?: boolean;
    reactions?: ReactionCount[];
    my_reaction?:string;
    is_pinned?: boolean;
    pinned_at?: string | null;
    is_deleted?: boolean;
    deleted_at?: string | null;
    reply_to?: {
        id?: number;
        sender_id?: string;
        sender_name?: string;
        type?: string;
        message?: string | null;
        file_url?: string | null;
        fid?: string | null;
        file_id?: string | null;
        file_fid?: string | null;
        media_fid?: string | null;
        file_name?: string | null;
        is_deleted?: boolean;
        created_at?: string;
    } | null;
    system_event?: ConversationSystemEvent | null;
}

export type ConversationSystemEventType =
    | "content_protection_enabled"
    | "content_protection_disabled";

export interface ConversationSystemEvent {
    type: ConversationSystemEventType;
    actor_id: string;
    actor_name: string;
}

export interface ConversationContentProtection {
    conversation_id: number;
    enabled: boolean;
    enabled_by: { id: string; name: string } | null;
    updated_at: string | null;
    can_manage: boolean;
}

export interface MessagesApiResponse {
    meta: any;
    data: MessageNewApi[];
}

export type ConversationPolicy = "mutual_like" | "compatibility" | null;

export interface CreateConversationResponse {
    meta: {
        status: number;
    };
    data: {
        chat_id: number;
        created: boolean;
    };
}

export interface ReactionCount {
    reaction: string
    count: number
}

export interface ToggleReactionResponse {
    toggled: 'set' | 'removed'
    reaction: string
    counts: ReactionCount[]
}

export interface SendMessagePayload {
    conversation_id: number;
    type: "text" | "contact" | "media" | "gift";
    message?: string;
    file?: File;
    gift?: string | null;
    contact_type?: string | null;
    reply_to_message_id?: number | null;
}

export interface SendMessageResponse {
    message: string;
    message_id: number;
    file_url: string | null;
    file_type: string | null;
    data: MessageNewApi;
}

export default class ConversationsSlice {
    private static basePath = "/api/conversations";

    /** Получить все разговоры */
    static async GetConversations(): Promise<Conversation | null> {
        const resp: AxiosResponse<Conversation> = await api.get(`${this.basePath}`, { params: { show_news: true } });
        return resp.data ?? null;
    }

    /** Закрепить/открепить диалог */
    static async PinConversation(id: number): Promise<void> {
        await api.post(`${this.basePath}/pin/${id}`);
    }

    /** Удалить диалог */
    static async DeleteConversation(id: number): Promise<void> {
        await api.delete(`${this.basePath}/${id}`);
    }

    /** Получить сообщения диалога */
    static async GetConversationById(id: number, page: number = 1): Promise<MessageNewApi[]> {
        console.log(`📡 API запрос: ${this.basePath}/${id}/messages/?page=${page}`);

        try {
            const resp: AxiosResponse<MessagesApiResponse> = await api.get(
                `${this.basePath}/${id}/messages/?page=${page}`
            );

            const messagesArray = resp.data?.data ?? [];

            console.log(`📨 API ответ для страницы ${page}:`, {
                url: `${this.basePath}/${id}/messages/?page=${page}`,
                status: resp.status,
                totalMessages: messagesArray.length,
                messages: messagesArray,
                fullResponse: resp.data
            });
            console.log(messagesArray,'messagesArray')


            return messagesArray.map((msg: MessageNewApi) => {
                const myReaction = msg.my_reaction ?? '';
                const reactions = [...(msg.reactions ?? [])];
                // Backend bug workaround: my_reaction may be missing from reactions array
                if (myReaction && !reactions.find(r => r.reaction === myReaction)) {
                    reactions.push({ reaction: myReaction, count: 1 });
                }
                return {
                    id: msg.id,
                    created_at: dayjs.utc(msg.created_at).add(3, "hour").toISOString(),
                    type: msg.type,
                    sender_id: msg.sender_id,
                    message: msg.message,
                    contact_type: msg.contact_type,
                    gift: msg.gift,
                    is_read: msg.is_read,
                    file_url: msg.file_url,
                    fid: msg.fid ?? null,
                    file_id: msg.file_id ?? null,
                    file_fid: msg.file_fid ?? null,
                    media_fid: msg.media_fid ?? null,
                    file_name: msg.file_name ?? null,
                    file_mime: msg.file_mime ?? null,
                    file_size: msg.file_size ?? null,
                    file_extension: msg.file_extension,
                    is_image: !!msg.is_image,
                    is_video: !!msg.is_video,
                    is_document: !!msg.is_document,
                    reactions,
                    my_reaction: myReaction,
                    is_pinned: !!msg.is_pinned,
                    pinned_at: msg.pinned_at ?? null,
                    is_deleted: !!msg.is_deleted,
                    deleted_at: msg.deleted_at ?? null,
                    reply_to: msg.reply_to ?? null,
                    system_event: msg.system_event ?? null,
                };
            }) as MessageNewApi[];


        } catch (error) {
            console.error('❌ Ошибка API GetConversationById:', error);
            throw error;
        }
    }

    /** Начать новый разговор */
    static async StartConversation(user_id: string) {
        const resp = await api.post(`${this.basePath}`, {user_id});
        return resp.data;
    }

    /** Отметить сообщения как прочитанные */
    static async ReadMessages(id: number): Promise<void> {
        await api.post(`${this.basePath}/read-messages/${id}`);
    }

    /** Отправка файла */
    static async SendFile(id: number, payload: FormData) {
        const resp = await api.post(`${this.basePath}/${id}/media`, payload, {
            headers: {"Content-Type": "multipart/form-data"},
        });
        return resp.data;
    }

    /** Получить статус непрочитанных сообщений */
    static async GetUnreadMessagesStatus(): Promise<{ has_unread_messages: number }> {
        const resp = await api.get(`${this.basePath}/unread-messages-status`);
        return resp.data ?? {has_unread_messages: 0};
    }

    static async PostNewConversation(
        userId: string,
        conversationPolicy?: ConversationPolicy
    ): Promise<CreateConversationResponse["data"] | null> {
        const resp: AxiosResponse<CreateConversationResponse> = await api.post(
            `${this.basePath}/`,
            {
                user_id: userId,
                conversation_policy: conversationPolicy ?? "mutual_like",
            }
        );

        return resp.data?.data ?? null;
    }

    static async ToggleReaction(
        chat_id: number,
        message_id: number,
        reaction: string
    ): Promise<ToggleReactionResponse> {

        try {
            const resp: AxiosResponse<{
                meta: {
                    status: number
                    error: any
                }
                data: ToggleReactionResponse
            }> = await api.post(
                `${this.basePath}/${chat_id}/messages/${message_id}/reactions`,
                { reaction }
            );

            console.log('👍 Reaction toggle response:', {
                status: resp.status,
                fullResponse: resp.data
            });

            return resp.data.data;

        } catch (error) {
            console.error('❌ Ошибка API ToggleReaction:', error);
            throw error;
        }
    }

    static async UpdateMessage(chat_id: number, message_id: number, message: string): Promise<MessageNewApi> {
        const resp: AxiosResponse<{
            meta: { status: number; error: any };
            data: MessageNewApi;
        }> = await api.patch(
            `${this.basePath}/${chat_id}/messages/${message_id}`,
            { message },
            { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );

        return resp.data.data;
    }

    static async PinMessage(chat_id: number, message_id: number): Promise<{ is_pinned: boolean; pinned_at: string | null }> {
        const resp: AxiosResponse<{
            meta: { status: number; error: any };
            data: { is_pinned: boolean; pinned_at: string | null };
        }> = await api.post(
            `${this.basePath}/${chat_id}/messages/${message_id}/pin`,
            undefined,
            { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );

        return resp.data.data;
    }

    static async DeleteMessage(chat_id: number, message_id: number): Promise<void> {
        await api.delete(
            `${this.basePath}/${chat_id}/messages/${message_id}`,
            { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );
    }

    static async GetContentProtection(chat_id: number): Promise<ConversationContentProtection> {
        const resp: AxiosResponse<{
            meta: { status: number; error: any };
            data: ConversationContentProtection;
        }> = await api.get(`${this.basePath}/${chat_id}/content-protection`);

        return resp.data.data;
    }

    static async SetContentProtection(
        chat_id: number,
        enabled: boolean
    ): Promise<ConversationContentProtection> {
        const resp: AxiosResponse<{
            meta: { status: number; error: any };
            data: ConversationContentProtection;
        }> = await api.put(
            `${this.basePath}/${chat_id}/content-protection`,
            { enabled },
            { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );

        return resp.data.data;
    }

    static async SendMessage(payload: SendMessagePayload): Promise<SendMessageResponse> {
        const formData = new FormData();
        formData.append("conversation_id", String(payload.conversation_id));
        formData.append("type", payload.type);

        if (payload.message !== undefined) formData.append("message", payload.message);
        if (payload.file) formData.append("file", payload.file);
        if (payload.gift !== undefined && payload.gift !== null) formData.append("gift", payload.gift);
        if (payload.contact_type !== undefined && payload.contact_type !== null) formData.append("contact_type", payload.contact_type);
        if (payload.reply_to_message_id !== undefined && payload.reply_to_message_id !== null) {
            formData.append("reply_to_message_id", String(payload.reply_to_message_id));
        }

        const resp: AxiosResponse<{
            meta: { status: number; error: any };
            data: SendMessageResponse;
        }> = await api.post(`${this.basePath}/send-messages`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return resp.data.data;
    }
}
